import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Grid,
  Button,
  Tab,
  Slide,
} from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import AddTrainingsCard from "../TrainingComponents/AddTrainingsCard";
import {
  GET_TRAINING_MODULES,
  ADD_TRAINING_MODULE_FROM_LIBRARY,
  UPDATE_TRAINING_MODULE_STATUS,
} from "../../../apis/training-apis/trainingApis";
import { useQuery, useMutation } from "@apollo/client";
import AddTrainingChoiceDialog from "../TrainingDialogs/AddTrainingChoiceDialog";
import ManualAddTrainingDialog from "../TrainingDialogs/ManualAddTrainingDialog";
import AddTrainingFromLibraryDialog from "../TrainingDialogs/AddTrainingFromLibraryDialog";
import TrainingDetailDialog from "../TrainingDialogs/TrainingDetailsDialog";
import AddIcon from "@mui/icons-material/Add";
import ConfirmSnackbar from "../../../components/Snackbar/ConfirmSnackbar";
import { TabNames, TrainingStep } from "../utils/enums";
import TrainingCard from "../TrainingComponents/TrainingCard";
import { TrainingModuleStatusKey } from "../utils/enums";
import { GET_MASTER_LOOKUP_BY_TYPE } from "../../../apis/common-apis/commonApis";
import { MasterLookupItemsType } from "../../../utils/enums";
import { useTheme } from "@mui/material/styles";
import InformationStepsCard from "../../../components/InformationStepsCard/InformationStepsCard";

const TrainingAdminHome = () => {
  const [addTrainingsFromLibrary] = useMutation(
    ADD_TRAINING_MODULE_FROM_LIBRARY
  );
  const [addTrainingStep, setAddTrainingStep] = useState(null); // null | TrainingStep.CHOICE | TrainingStep.LIBRARY | TrainingStep.MANUAL
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [tabName, setTabName] = useState(TabNames.DRAFT_TRAININGS); // Will be updated after data loads
  const [tab, setTab] = useState("1"); // Will be updated after data loads
  const [updateTrainingModuleStatus] = useMutation(
    UPDATE_TRAINING_MODULE_STATUS
  );
  const theme = useTheme();

  const {
    data: trainingModulesData,
    loading: isTrainingModulesDataLoading,
    refetch,
  } = useQuery(GET_TRAINING_MODULES, {
    variables: { page: 1, limit: 1000 },
  });

  const { data: masterLookup, loading: masterLookupLoading } = useQuery(
    GET_MASTER_LOOKUP_BY_TYPE,
    {
      variables: { type: MasterLookupItemsType.TRAINING_MODULE_STATUS },
    }
  );

  const { data: trainingSectionLookup, loading: trainingSectionLookupLoading } =
    useQuery(GET_MASTER_LOOKUP_BY_TYPE, {
      variables: { type: MasterLookupItemsType.TRAINING_SECTION },
    });

  // Process data and set up filtering
  const masterLookupData = masterLookup?.getMasterLookupByType?.data ?? [];
  const trainingModuleStatusMap = Object.fromEntries(
    masterLookupData?.map((item) => [item?.key, item?.id]) ?? []
  );

  const trainingSectionLookupData =
    trainingSectionLookup?.getMasterLookupByType?.data ?? [];
  const trainingSectionMap = Object.fromEntries(
    trainingSectionLookupData?.map((item) => [item?.key, item?.title]) ?? []
  );

  const trainingModules = trainingModulesData?.getTrainingModules?.data ?? [];

  // Reusable function to filter trainings by status
  const getTrainingsByStatus = (statusKey) =>
    trainingModules?.filter((data) => data?.status_key === statusKey) ?? [];

  const draftTrainings = getTrainingsByStatus(TrainingModuleStatusKey.DRAFT);
  const inactiveTrainings = getTrainingsByStatus(
    TrainingModuleStatusKey.INACTIVE
  );
  const activeTrainings = getTrainingsByStatus(TrainingModuleStatusKey.ACTIVE);

  // Calculate initial tab based on whether there are active trainings
  const getInitialTabState = () => {
    if (activeTrainings.length > 0) {
      return { tab: "1", tabName: TabNames.ACTIVE_TRAININGS };
    } else {
      return { tab: "1", tabName: TabNames.DRAFT_TRAININGS };
    }
  };

  // Update tab state after data loads
  useEffect(() => {
    const initialTabState = getInitialTabState();
    setTabName(initialTabState.tabName);
    setTab(initialTabState.tab);
  }, [activeTrainings.length]); // Re-run when activeTrainings changes

  const TransitionComponent = (props) => {
    return <Slide {...props} direction="left" />;
  };

  // Add Trainings from master library
  const handleAddTrainings = async (selectedModules) => {
    try {
      const moduleIds = selectedModules?.map((module) => module?.id) ?? [];
      const { data } = await addTrainingsFromLibrary({
        variables: { training_module_ids: moduleIds },
      });
      if (data?.addTrainingModuleFromLibrary?.success) {
        await refetch?.(); //  Refetch updated training list
        setSnackbarInfo({
          message: "Training courses added successfully!",
          vertical: "top",
          horizontal: "right",
          Transition: TransitionComponent,
        });
      } else {
        console.error(
          "Failed to add training courses:",
          data?.addTrainingModuleFromLibrary?.message
        );
        setSnackbarInfo({
          message: "Failed to add training courses!",
          vertical: "top",
          horizontal: "right",
          Transition: TransitionComponent,
        });
      }
    } catch (error) {
      console.error("Error adding training courses from library:", error);
    }
  };

  const handleUpdateTrainingModuleStatus = async (id, statusId) => {
    try {
      const message = await updateTrainingModuleStatus({
        variables: { id, status_id: statusId },
      });

      const response = message?.data?.updateTrainingModuleStatus;

      if (response?.success) {
        return true; // Indicate success to child
      } else {
        console.error("Failed to update status of course:", response?.message);
        return false;
      }
    } catch (error) {
      console.error("Error in updating the status of course", error);
      return false;
    }
  };

  if (
    isTrainingModulesDataLoading ||
    masterLookupLoading ||
    trainingSectionLookupLoading
  ) {
    return (
      <Container
        component="main"
        maxWidth="xs"
        sx={{ textAlign: "center", mt: 8 }}
      >
        <CircularProgress />
      </Container>
    );
  }

  // Handler for Add New Training button
  const handleAddTrainingClick = () => setAddTrainingStep(TrainingStep.CHOICE);

  // Handler for choice dialog
  const handleAddTrainingChoice = (option) => {
    if (option === TrainingStep.LIBRARY)
      setAddTrainingStep(TrainingStep.LIBRARY);
    else if (option === TrainingStep.MANUAL)
      setAddTrainingStep(TrainingStep.MANUAL);
  };

  // Handler to close any add Training dialog
  const handleAddTrainingDialogClose = () => setAddTrainingStep(null);

  // Handle view details for Training Register
  const handleDetailDialogOpen = (training) => {
    if (training) {
      setSelectedTraining(training);
      setIsDetailsDialogOpen(true);
    }
  };

  // Clear selected Training when detail dialog closes
  const handleDetailDialogClose = () => {
    setIsDetailsDialogOpen(false);
    setSelectedTraining(null);
  };

  // Dynamic tab names based on whether there are active trainings
  const getTabNames = () => {
    const activeTrainings = getTrainingsByStatus(
      TrainingModuleStatusKey.ACTIVE
    );

    if (activeTrainings.length > 0) {
      return {
        1: TabNames.ACTIVE_TRAININGS,
        2: TabNames.DRAFT_TRAININGS,
        3: TabNames.INACTIVE_TRAININGS,
      };
    } else {
      return {
        1: TabNames.DRAFT_TRAININGS,
        2: TabNames.ACTIVE_TRAININGS,
        3: TabNames.INACTIVE_TRAININGS,
      };
    }
  };

  const tabNames = getTabNames();

  const handleChange = (event, newTab) => {
    setTab(newTab);
    setTabName(tabNames[newTab]);
  };

  // Reusable component for Active Training content
  const ActiveTrainingContent = () =>
    (activeTrainings?.length ?? 0) > 0 ? (
      <>
        <Typography
          variant="body1"
          sx={{ color: "text.secondary", marginBottom: "20px" }}
        >
          You can view all active training courses here that are currently live
          and accessible to employees.
        </Typography>
        <Grid container spacing={2}>
          {activeTrainings?.map((training, index) => (
            <Grid item xs={6} key={training?.id ?? index}>
              <TrainingCard
                training={training}
                isAdmin={true}
                onClick={handleDetailDialogOpen}
              />
            </Grid>
          ))}
        </Grid>
      </>
    ) : (
      <Box
        sx={{
          height: "300px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          width: "100%",
        }}
      >
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          No active training courses available.
          <br /> Activate some from the draft tab to make them visible to
          employees.
        </Typography>
      </Box>
    );

  // Reusable component for Draft Training content
  const DraftTrainingContent = () =>
    (draftTrainings?.length ?? 0) > 0 ? (
      <>
        <Typography
          variant="body1"
          sx={{ color: "text.secondary", marginBottom: "20px" }}
        >
          You can draft new training courses here and can activate and
          deactivate them.
        </Typography>
        <Grid container spacing={2}>
          {draftTrainings?.map((training, index) => (
            <Grid item xs={6} key={training?.id ?? index}>
              <TrainingCard
                key={training?.id ?? index}
                training={training}
                isAdmin={true}
                onClick={handleDetailDialogOpen}
              />
            </Grid>
          ))}
        </Grid>
      </>
    ) : (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "60vh",
          width: "100%",
        }}
      >
        <Box
          sx={{
            height: "50vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            width: "100%",
          }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            No draft training courses yet.
            <br /> Use the button to add a new one.
          </Typography>
        </Box>
        <AddTrainingsCard handleAddTrainingClick={handleAddTrainingClick} />
      </Box>
    );

  // Reusable component for Inactive Training content
  const InactiveTrainingContent = () =>
    (inactiveTrainings?.length ?? 0) > 0 ? (
      <>
        <Typography
          variant="body1"
          sx={{ color: "text.secondary", marginBottom: "20px" }}
        >
          You can view training courses here that have been deactivated and are
          currently hidden from employees.
        </Typography>
        <Grid container spacing={2}>
          {inactiveTrainings?.map((training, index) => (
            <Grid item xs={6} key={training?.id ?? index}>
              <TrainingCard
                training={training}
                isAdmin={true}
                onClick={handleDetailDialogOpen}
              />
            </Grid>
          ))}
        </Grid>
      </>
    ) : (
      <Box
        sx={{
          height: "300px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          width: "100%",
        }}
      >
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          No inactive training courses found.
          <br /> Once a module is deactivated, it will appear here.
        </Typography>
      </Box>
    );

  return (
    <>
      <Box sx={{ paddingTop: "20px" }}>
        <Box>
          <TabContext value={tab}>
            <Box
              sx={{ mb: 1, display: "flex", justifyContent: "space-between" }}
            >
              <Typography
                variant="h4"
                fontWeight="600"
                color={theme.customColors.text.primary}
                sx={{ mb: 0.5 }}
              >
                {tabName}
              </Typography>
              {tab === "1" &&
                tabNames[1] === TabNames.DRAFT_TRAININGS &&
                (draftTrainings?.length ?? 0) > 0 && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    onClick={handleAddTrainingClick}
                    sx={{
                      fontWeight: 500,
                      fontSize: "14px",
                      px: 2,
                      py: 1,
                    }}
                  >
                    Add Training Courses
                  </Button>
                )}
            </Box>

            {/* Information Steps Card */}
            <Box sx={{ mb: 3, mt: 3 }}>
              <InformationStepsCard
                heading="**What should I do on this page?**"
                subtitle="This page allows you to manage training courses for your employees, including creating, activating, and organizing training modules."
                steps={[
                  "**Active Trainings** tab: View all training courses that are currently live and accessible to employees. Employees can access and complete these trainings.",

                  "**Draft Trainings** tab: Create and manage training courses that are in draft status. You can add new trainings from the library, create them manually, or edit existing drafts before activating them.",

                  "**Inactive Trainings** tab: View training courses that have been deactivated and are hidden from employees. You can reactivate them if needed.",

                  'Use the **"Add Training Courses"** button (when on Draft tab) to add new training courses. You can choose to add from the library or create manually.',

                  "Click on any **training card** to view detailed information, edit the training module, manage training materials, and activate or deactivate the training.",

                  "Activate draft trainings when they're ready to be made available to employees, or deactivate active trainings if they need to be updated or removed temporarily.",
                ]}
                footerText="💡 Regularly review and update your training courses to ensure employees have access to current and relevant security training materials."
                collapsible={true}
                defaultExpanded={false}
              />
            </Box>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <TabList onChange={handleChange} aria-label="Training Courses">
                <Tab
                  label={`${tabNames[1]} (${tabNames[1] === TabNames.ACTIVE_TRAININGS
                      ? activeTrainings?.length ?? 0
                      : draftTrainings?.length ?? 0
                    })`}
                  value="1"
                />
                <Tab
                  label={`${tabNames[2]} (${tabNames[2] === TabNames.ACTIVE_TRAININGS
                      ? activeTrainings?.length ?? 0
                      : draftTrainings?.length ?? 0
                    })`}
                  value="2"
                />
                <Tab
                  label={`${TabNames.INACTIVE_TRAININGS} (${inactiveTrainings?.length ?? 0
                    })`}
                  value="3"
                />
              </TabList>
            </Box>
            <TabPanel value="1" sx={{ p: 0 }}>
              {tabNames[1] === TabNames.ACTIVE_TRAININGS ? (
                <ActiveTrainingContent />
              ) : (
                <DraftTrainingContent />
              )}
            </TabPanel>
            <TabPanel value="2" sx={{ p: 0 }}>
              {tabNames[2] === TabNames.ACTIVE_TRAININGS ? (
                <ActiveTrainingContent />
              ) : (
                <DraftTrainingContent />
              )}
            </TabPanel>
            <TabPanel value="3" sx={{ p: 0 }}>
              <InactiveTrainingContent />
            </TabPanel>
          </TabContext>
        </Box>
      </Box>
      {/* Add Training Choice Dialog */}
      {addTrainingStep === TrainingStep.CHOICE && (
        <AddTrainingChoiceDialog
          open={addTrainingStep === TrainingStep.CHOICE}
          onClose={handleAddTrainingDialogClose}
          onSelectOption={handleAddTrainingChoice}
        />
      )}
      {addTrainingStep === TrainingStep.LIBRARY && (
        <AddTrainingFromLibraryDialog
          open={addTrainingStep === TrainingStep.LIBRARY}
          onClose={handleAddTrainingDialogClose}
          onAddTrainings={handleAddTrainings}
        />
      )}
      {addTrainingStep === TrainingStep.MANUAL && (
        <ManualAddTrainingDialog
          open={addTrainingStep === TrainingStep.MANUAL}
          onClose={handleAddTrainingDialogClose}
        />
      )}

      {isDetailsDialogOpen && (
        <TrainingDetailDialog
          open={isDetailsDialogOpen}
          onClose={handleDetailDialogClose}
          training={selectedTraining}
          onStatusChange={handleUpdateTrainingModuleStatus}
          refetch={refetch}
          trainingModuleStatusMap={trainingModuleStatusMap}
          trainingSectionMap={trainingSectionMap}
          setSnackbarInfo={setSnackbarInfo}
        />
      )}
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default TrainingAdminHome;
